import CountDown from "@components/ui/Timer";
import { yupResolver } from "@hookform/resolvers/yup";
import { loginUser, prevUrlBeforAuth, setProfile } from "@store/reducers/auth";
import Refresh from "assets/svg/auth/refresh.svg";
import { Button, Input, Skeleton } from "components";
import Modal from "components/ui/Modal";
import gate from "gate";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { toEnDigit, toEnglishDigits } from "src/utils";
import { mobileRegExp } from "src/utils/validation";
import { makeStore } from "store";
import * as yup from "yup";

import { CampaignType } from "../../../../enums/campaign-type";

interface PageProps {
  mode: "mobile" | "desktop";
  setPage: React.Dispatch<React.SetStateAction<1 | 2 | 3>>;
  page?: 1 | 2 | 3;
}

const Login: FC<PageProps> = ({ mode, setPage, page }) => {
  const router = useRouter();
  const dispatch = makeStore().dispatch;
  const { mutate: createUserActivityMutate } = useMutation(
    gate.createUserActivity
  );
  const [loginData, setLoginData]: any = useState();
  const schema = yup.object().shape({
    ...(page == 1 && {
      mobile: yup
        .string()
        .trim()
        .required("این فیلد نمیتواند خالی باشد.")
        .matches(mobileRegExp, {
          message: "شماره موبایل وارد شده صحیح نمیباشد.!",
        }),
    }),
    ...(page == 2 && {
      code: yup.string().trim().required("این فیلد نمیتواند خالی باشد."),
    }),
    ...(page == 3 && {
      name: yup.string().trim().required("این فیلد نمیتواند خالی باشد."),
      family: yup.string().trim().required("این فیلد نمیتواند خالی باشد."),
      hasReadRules: yup
        .boolean()
        .oneOf([true], "پذیرفتن قوانین مقررات اجباری است."),
    }),
  });
  const { mutateAsync: register, isLoading } = useMutation(gate.register);
  const {
    data: captchaData,
    mutateAsync: getCaptcha,
    isLoading: loadingCaptcha,
  }: any = useMutation(gate.captcha);
  const { mutate: updateProfile, isLoading: isLoadingUpdateProfile } =
    useMutation(gate.updateProfile, {
      onSuccess: (data) => {
        dispatch(setProfile(data));
      },
      onError: (error: any) => {
        if (!error.handleGlobally) {
          error?.data?.message
            ? toast.error(error?.data?.message, { position: "bottom-center" })
            : console.log(error);
        }
      },
    });
  const {
    handleSubmit,
    control,
    setValue,
    register: registerForm,
    formState: { errors },
  } = useForm<any>({ resolver: yupResolver(schema) });
  const { prevUrl } = useSelector((state: any) => state?.auth);
  const [captchaCode, setCaptchaCode] = useState<any>();
  const [needCaptcha, setNeedCaptcha] = useState(false);
  const { isLoading: authVerifyLoading, mutate: authVerifyToken } = useMutation(
    gate.verifyToken
  );
  const { isLoading: authSendCodeLoading, mutate: authSendCode } = useMutation(
    gate.sendCode
  );
  const [isAllowToSendAgain, setIsAllowToSendAgain] = useState<boolean>(false);
  const [countRender, setCountRender] = useState(1);
  const [mobile, setMobile] = useState<number | string>(0);
  const [showModal, setShowModal] = useState(false);
  const { referralCode } = router.query;
  useEffect(() => {
    setValue("invitationCode", referralCode);
  }, [router.query]);

  const submit = (e: any) => {
    if (page == 1) {
      register(
        { phone: e.mobile },
        {
          onSuccess: () => {
            setPage(2);
            setMobile(e.mobile);
          },
          onError: (e: any) => {
            if (!e.handleGlobally) {
              toast.error(e?.data?.message, {
                position: mode == "desktop" ? "bottom-right" : "bottom-center",
              });
            }
          },
        }
      );
    } else if (page == 2) {
      const formData = {
        verifyCode: e.code,
        phone: mobile,
        twoFactorMethod: 1,
        ...(needCaptcha && { captchaCode: captchaCode }),
        ...(needCaptcha && { captchaText: captchaData?.captchaText }),
        ...(needCaptcha && { captchaToken: captchaData?.captchaToken }),
      };
      authSendCode(formData, {
        onSuccess: (e: any) => {
          let campaign: any = localStorage.getItem("campaign");
          if (campaign && JSON.parse(campaign)) {
            campaign = JSON.parse(campaign);
            createUserActivityMutate({
              campaignId: +campaign.campaign_id,
              followUpId: +campaign.followup_id,
              phoneNumber: campaign.phone_number,
              type: +CampaignType.Registered,
              url: campaign.url,
              isPromotion: campaign?.isPromotion.toLowerCase() === "true",
            });
          }
          if (e?.token?.need_update_profile) {
            setLoginData(e);
            setPage(3);
          } else {
            dispatch(loginUser(e));
            !!prevUrl &&
              dispatch(prevUrlBeforAuth({ path: prevUrl, needRedirect: true }));
            setTimeout(() => {
              !!prevUrl
                ? router.push(prevUrl)
                : router.push("/student/dashboard");
            }, 100);
          }
        },
        onError: (e: any) => {
          if (!e.handleGlobally) {
            toast.error(e?.data?.message, {
              position: mode == "desktop" ? "bottom-right" : "bottom-center",
            });
            setCaptchaCode(null);
            setNeedCaptcha(false);
            if (e.status == 499) {
              getCaptcha();
              setNeedCaptcha(true);
              setShowModal(true);
            }
          }
        },
      });
    } else {
      updateProfile(
        {
          email: e.email.trim(),
          firstName: e.name.trim().replaceAll("‌", ""),
          lastName: e.family.trim().replaceAll("‌", ""),
          invitationCode: e.invitationCode.trim(),
          header: { Authorization: `Bearer ${loginData.token.access_token}` },
        },
        {
          onSuccess: () => {
            dispatch(loginUser(loginData));
            setTimeout(() => {
              document.body.classList.remove("overflow-hidden");
              !!prevUrl
                ? router.push(prevUrl)
                : router.push("/student/dashboard");
            }, 0);
          },
        }
      );
    }
  };

  const handleResendCode = () => {
    authVerifyToken(
      { phoneNumber: mobile, twoFactorMethod: 2 },
      {
        onSuccess: () => {
          setCountRender((prev) => prev + 1);
          setIsAllowToSendAgain(false);
        },
        onError: (err: any) => {
          if (!err.handleGlobally) {
            toast.error(err.data.message);
          }
        },
      }
    );
  };
  const [isShowModal, setIsShowModal] = useState(false);

  return (
    <>
      <Head>
        <title>ورود/ثبت نام</title>
      </Head>{" "}
      <Modal
        visible={isShowModal}
        onClose={() => setIsShowModal(false)}
        className="top-0 !z-[9999999999] h-full w-screen"
      >
        <form className="relative mx-auto grid h-full w-full grid-rows-[_1fr,_10fr] flex-col rounded bg-white py-6 md:h-[90vh] md:w-4/5 md:py-[30px] 2xl:w-[50%]">
          <div className="mb-5 flex justify-between border-b pb-4 m-h-500:mb-0">
            <p className="mr-5 self-center whitespace-nowrap text-xs text-[#4E4E56] md:text-lg">
              قوانین سایت دادبانان
            </p>
            <span
              className="ml-5 rotate-45 cursor-pointer text-2xl text-gray-400"
              onClick={() => setIsShowModal(false)}
            >
              +
            </span>
          </div>
          <div className="flex w-full flex-col justify-between overflow-y-auto md:grid">
            <div className="flex flex-col gap-y-5 overflow-y-auto px-5 text-[#4E4E56]">
              <div className="text-justify text-sm md:text-lg">
                <b>به سایت دادبانان خوش آمدید.</b>
              </div>

              <p className="space-y-1 text-justify text-xs leading-relaxed tracking-tight md:text-base">
                آموزشگاه دادبانان با ایجاد شرایط و امکانات مناسب، امکان برگزاری
                کارگاه‌های آنلاین، آفلاین و حضوری حقوقی و کاربردی را فراهم نموده
                است.
                <br />
                ‌برای ثبت نام در دوره‌های آموزشی دادبانان لطفاً کلیه موارد زیر
                را به دقت مطالعه فرمایید.
              </p>
              <ul className="grid list-inside list-decimal gap-y-3">
                <p>پرداخت، به معنای پذیرش کلیه قوانین و تعهدات زیر است.</p>
                <li>
                  از آن‌جایی که برخی از وبینارها و کارگاه‌های آموزشی با ارائه
                  گواهی پایان دوره همراه است؛ لطفاً در زمان ثبت نام در سایت
                  دادبانان، اطلاعات شخصی خود از جمله نام و نام خانوادگی، آدرس
                  ایمیل و شماره موبایل را به درستی و دقیق وارد کنید، چرا که این
                  اطلاعات ملاک صدور گواهی‌نامه‌هاست.
                </li>
                <li>
                  هرگونه تغییر یا اشتباهی که در مشخصات ثبت نامی، اعم از آدرس و
                  نام و نام خانوادگی و ... پیش‌آمده را در اسرع وقت و قبل از
                  پایان جلسه اول به مسئولین ثبت نام اطلاع دهید، در غیر این‌صورت
                  مسئولیت نادرستی اطلاعات چاپ شده در گواهی نامه‌ها بر عهده شخص
                  دانش‌پذیر است.
                </li>
                <li>
                  در صورت پرداخت مبلغ اعلامی هر کارگاه یا دوره آنلاین یا آفلاین
                  می‌توانید، به فیلم‌ها دسترسی داشته باشید.
                </li>
                <li>
                  در صورت ثبت نام قسطی، در موعد مقرر نسبت به تسویه اقساطی خود
                  اقدام فرمایید، در غیر اینصورت دادبانان حق دارد، دسترسی
                  دانشپذیر به ادامه کارگاه را متوقف نماید و مدرک برای آن
                  کارگاه‌ها صادر نمی‌شود.
                </li>
                <li>
                  چنان‌چه استاد دوره، فایل پاورپوینت یا PDF یا هر فایل دیگری را
                  برای ارائه درس استفاده کند، این فایل پس از موافقت استاد در
                  داشبورد دانش‌پذیران قرار خواهد گرفت.
                </li>
                <li>
                  لطفاً در اسرع وقت، نسبت به مشاهده فیلم کارگاه‌ها اقدام
                  فرمایید، دسترسی دانشپذیر به فایل‌ها، <b>حداکثر تا یک سال</b>{" "}
                  پس از اتمام کارگاه (آنلاین) یا خرید (آفلاین) امکان‌پذیر
                  می‌باشد و پس از آن نیز تحت شرایطی، با پرداخت حق اشتراک در
                  دسترس دانش‌پذیران خواهد بود.
                </li>
                <li>
                  برای دوره‌های آنلاین نیم ساعت زودتر در کلاس حضور داشته باشید،
                  تا از تست صدا و تصویر اطمینان حاصل نمایید.
                </li>
                <li>
                  برای رفع اشکال و سایر موارد در مورد کارگاه‌ها در "صورت نیاز"
                  گروه‌های مجازی تشکیل می‌شود، از برقراری ارتباط با سایر کاربران
                  در خارج از محیط گروه جداً اجتناب نمایید.
                </li>
                <li>
                  لطفا گروه مجازی کارگاه را چک بفرمایید، تا هرگونه تغییر در
                  برنامه‌ریزی کارگاه و زمان دریافت مدارک و غیره را متوجه شوید.
                </li>
                <li>
                  در‌صورتی‌که برای کلاس‌های حضوری ثبت نام کرده‌اید، رأس ساعت
                  مقرر به کلاس وارد شوید و نظم حضور را رعایت فرمایید.
                </li>
                <li>
                  از ارتباط با سایر پرسنل محل برگزاری کلاس‌های حضوری جداً اجتناب
                  نموده و پرسش‌هایتان را با مسئولین آموزشی دادبانان مطرح
                  بفرمایید.
                </li>
                <li>
                  در طول دوره‌ها از دانشپذیران نظرسنجی می‌گردد و تحویل مدارک به
                  دانشپذیران منوط به شرکت در نظر‌سنجی است. مجموعه دادبانان این
                  امکان را دارد که از نظرسنجی داوطلبین به نحو مقتضی استفاده
                  نماید.
                </li>
                <li>
                  داوطلبین گرامی می‌بایست ظرف یک ماه بعد از اعلام صدور مدارک
                  کارگاه، درباره «کارگاه‌های مشمول مدرک»، با هماهنگی قبلی نسبت
                  به مراجعه حضوری به نشانی خیابان پاسداران، خیابان کلاهدوز، بین
                  دیباجی و یار محمدی، نبش حافظ (طالقانی) پلاک 1، طبقه دوم، واحد
                  سوم (آموزشگاه دادبانان دانا) و دریافت مدارک آن دوره اقدامات
                  لازم را به عمل آورند، در غیر این‌صورت دادبانان مسئولیتی جهت
                  نگهداری و تحویل مدارک ندارد.
                </li>
                <li>
                  درصورت توافق برای پست مدارک، پرداخت هزینه پستی بر عهده
                  دانشپذیر است.
                </li>
                <li>
                  در هر موردی که قرار است به دانشپذیر مدرکی در پایان دوره مبنی
                  بر حضور وی در دوره اعطا شود و مرجع صدور مدرک، به هر دلیلی به
                  تعهد خویش عمل نکند، دادبانان ۲۰ درصد مبلغ پرداختی دانشپذیر را،
                  حداکثر یک هفته پس از درخواست مکتوب دانشپذیر، به وی مسترد
                  مینماید و دانشپذیر حق و ادعایی علیه دادبانان یا نهاد دیگری
                  ندارد؛ بدیهی است دادبانان حق مراجعه به مرجع صدور مدرک که به
                  تعهد خویش عمل ننموده را دارد.
                </li>
                <li>
                  کلیه محتواهای مکتوب، صوتی و تصویری صرفا جهت استفاده شخص ثبت
                  نام کننده مجاز است و هرگونه دانلود، تکثیر یا انتشار یا عرضه آن
                  ها ممنوع و نقض حقوق پدیدآورنده محسوب شده و با مرتکب طبق قوانین
                  حمایت از مالکیت فکری از جمله قانون حمایت مولفان، مصنفان و
                  هنرمندان مصوب ۱۳۴۸ و قانون ترجمه و تکثیر کتب و نشریات و آثار
                  صوتی مصوب ۱۳۵۲ و قانون تجارت الکترونیکی مصوب ۱۳۸۲ برخورد خواهد
                  شد.
                </li>
              </ul>
              <ul className="grid list-inside list-disc gap-y-3">
                <p>لازم به ذکر است که: </p>
                <li>
                  دادبانان، اطلاعات خصوصی کاربران را در مجموعه دادبانان محافظت
                  می‌نماید.
                </li>
                <li>
                  آدرس ایمیل و شماره موبایل شرکت کنندگان در دوره‌ها به منظور
                  اطلاع‌رسانی دوره‌ها و کارگاه‌های آموزشی بعدی دادبانان و نیز
                  اعلام زمان ارائه مدرک پایان دوره مورد استفاده خواهد بود.
                </li>
                <li>
                  در حفظ نام کاربری و رمز عبور خود دقت لازم را به‌عمل آورید و
                  اطلاعات کاربری خود را در اختیار دیگران قرار ندهید، زیرا{" "}
                  <b>
                    تمامی فایل‌هایی که در پروفایل کاربری شما قرار می‌گیرند، به
                    صورت انحصاری کدنویسی شده‌اند و در‌صورتی‌که گذرواژه خود را در
                    اختیار دیگری قرار داده و فایل‌ها از پروفایل شما منتشر شود،
                    مسئولیت انتشار غیرقانونی فایل‌ها با صاحب پروفایل مزبور است.
                  </b>
                </li>
              </ul>
              <p>
                لطفا پس از ثبت نام در صورت هرگونه ابهام با پرسش با شماره{" "}
                <a href="tel:09104502525" className="mx-1 text-blue">
                  09104502525
                </a>
                بخش آموزش دادبانان، تماس حاصل نمایید؛ همکاران ما پاسخگوی شما
                خواهند بود.
              </p>
            </div>
          </div>
        </form>
      </Modal>
      <div className="flex w-full flex-col justify-between px-0 m-h-400:h-auto">
        <form
          className={`flex h-full flex-col justify-evenly ${
            page === 1 ? "space-y-5" : "space-y-2"
          } md:space-y-8 lg:space-y-2 xl:space-y-6 m-h-400:space-y-1`}
          onSubmit={handleSubmit(submit)}
        >
          {page == 2 && (
            <>
              <div className="space-y-3">
                <Input
                  name="code"
                  autoFocus
                  onChange={(e: any) => {
                    setValue("code", toEnglishDigits(e?.target?.value));
                  }}
                  error={errors?.code?.message}
                  control={control}
                  title={"کد"}
                  type="tel"
                  min={0}
                />
              </div>
              <div className="flex w-full items-center justify-between">
                <small
                  className={
                    isAllowToSendAgain
                      ? "cursor-pointer text-blue"
                      : "pointer-events-none cursor-not-allowed text-gray-200"
                  }
                  onClick={handleResendCode}
                >
                  {"ارسال دوباره"}
                </small>
                <div key={countRender}>
                  <CountDown
                    hours={0}
                    minutes={2}
                    seconds={0}
                    onlySecond={true}
                    whenOver={() => setIsAllowToSendAgain(true)}
                  />
                </div>
              </div>
            </>
          )}
          {needCaptcha && page == 2 && mode == "desktop" && (
            <div className="flex">
              <Input
                title="کد امنیتی"
                control={control}
                required
                onChange={(e: any) => setCaptchaCode(toEnDigit(e.target.value))}
                name="captchaCode"
                value={captchaCode}
                type="tel"
                className="w-1/2"
              />
              <div className="w-2" />
              <div className="flex w-1/2 items-center">
                {loadingCaptcha ? (
                  <Skeleton width={88} heigth={32} />
                ) : (
                  <img
                    loading="lazy"
                    src={captchaData?.captchaImgUrl}
                    alt="کپچا"
                    className="flex h-8 w-20 items-center rounded-lg shadow"
                  />
                )}

                <div
                  className="mr-2 flex h-8 w-8 items-center rounded-lg p-2 shadow"
                  onClick={() => getCaptcha()}
                >
                  <Refresh />
                </div>
              </div>
            </div>
          )}
          {page == 1 && (
            <Input
              name="mobile"
              onChange={(e: any) => {
                setValue("mobile", toEnglishDigits(e?.target?.value));
              }}
              error={errors?.mobile?.message}
              control={control}
              autoFocus
              autoComplete="off"
              title={"شماره تلفن"}
              type="tel"
            />
          )}
          {page == 3 && (
            <div className="grid grid-cols-1 gap-y-5">
              <Input
                name="name"
                error={errors?.name?.message}
                control={control}
                onChange={(event: any) => {
                  setValue("name", event.target.value.replaceAll("‌", ""));
                }}
                autoFocus
                autoComplete="off"
                title={"نام *"}
                type="text"
              />
              <Input
                name="family"
                error={errors?.family?.message}
                control={control}
                onChange={(event: any) => {
                  setValue("family", event.target.value.replaceAll("‌", ""));
                }}
                autoFocus
                autoComplete="off"
                title={"نام خانوادگی *"}
                type="text"
              />
              <Input
                name="invitationCode"
                control={control}
                autoFocus
                autoComplete="off"
                title={"کد معرف"}
                type="text"
              />
              <Input
                name="email"
                control={control}
                autoFocus
                autoComplete="off"
                title={"ایمیل"}
                type="email"
              />
              <div className="flex items-start gap-x-2">
                <input
                  type="checkbox"
                  {...registerForm("hasReadRules")}
                  name="hasReadRules"
                  className="h-4 w-4"
                />
                <span
                  className={`cursor-pointer text-xs text-black-450 ${
                    errors?.hasReadRules?.message &&
                    "eaccent-red-500 text-red-500"
                  }`}
                >
                  <span
                    className={`text-blue ${
                      errors?.hasReadRules?.message &&
                      "eaccent-red-500 text-red-500"
                    }`}
                    onClick={() => setIsShowModal(true)}
                  >
                    قوانین و مقررات
                  </span>{" "}
                  دادبانان را پذیرفته‌ام.
                </span>
              </div>
            </div>
          )}
          <div className="mt-4 flex w-full flex-col justify-center m-h-400:mt-0">
            <Button
              isLoading={
                isLoading || authSendCodeLoading || isLoadingUpdateProfile
              }
              type="submit"
              className="h-[34px] w-full rounded bg-blue py-1 px-2 text-white outline-none transition-all hover:opacity-90 focus-visible:outline-none md:h-12"
            >
              {"ورود"} / {"ثبت نام"}
            </Button>
          </div>
        </form>
      </div>
      {mode == "mobile" && (
        <Modal
          visible={showModal}
          className="h-screen w-screen"
          onClose={() => setShowModal(false)}
        >
          <div className="flex h-12 items-center bg-blue px-4">
            <small className="items-center text-white">کد امنیتی</small>
          </div>
          <div className="flex h-80 w-full flex-col justify-around bg-white px-4">
            <div className="space-y-2">
              <small>برای ادامه کد امنیتی داخل تصویر را وارد کنید</small>
              <small>تعداد رمز عبور اشتباه وارد شده بیش از حد مجاز است</small>
            </div>
            <div className="flex items-center justify-between">
              <div className="w-1/2">
                <Input
                  title="کد امنیتی"
                  control={control}
                  required
                  onChange={(e: any) => setCaptchaCode(e.target.value)}
                  name="captchaCode"
                  value={captchaCode || ""}
                  type="text"
                  className="w-1/2"
                />
                <div className="w-2" />
              </div>
              <div className="flex w-1/2 items-center justify-center">
                {loadingCaptcha ? (
                  <Skeleton width={88} heigth={32} />
                ) : (
                  <img
                    src={captchaData?.captchaImgUrl}
                    alt="کپچا"
                    loading="lazy"
                    className="flex h-8 w-20 items-center rounded-lg shadow"
                  />
                )}

                <div
                  className="mr-2 flex h-8 w-8 items-center rounded-lg p-2 shadow"
                  onClick={() => getCaptcha()}
                >
                  <Refresh />
                </div>
              </div>
            </div>
            <Button
              onClick={() => setShowModal(false)}
              className="h-9 w-8/12 self-center rounded-full border border-blue text-blue"
            >
              تایید
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Login;
